<script setup>
import SitemapHeader from "./includes/SitemapHeader";
import SitemapTable from "./includes/SitemapTable";
import PrimaryButton from "../global/buttons/PrimaryButton";
import {useStore} from "vuex";
import DefaultButton from "../global/buttons/DefaultButton.vue";
import ReviewSitemapKeywordsModal from "./modals/ReviewSitemapKeywordsModal.vue";
import SitemapProcessingKeywords from "@/components/sitemap/includes/SitemapProcessingKeywords.vue";
import useWebsite from "@/composables/useWebsite";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {computed, onMounted} from "vue";
import SitemapKitChangesModal from "@/components/sitemap/modals/SitemapKitChangesModal.vue";
import useKit from "@/composables/useKit";

const queryClient = useQueryClient();

const {mutate: acknowledgedProgressCompleted} = useMutation({
  mutationFn: () => axios.patch('/ajax/v2/strategy/mark-acknowledged-completion'),
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['auto-assign-status'] });
  },
});

const {data: keywordAutoAssignStatus } = useQuery({
  queryKey: ['auto-assign-status'],
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/strategy/auto-assign-status');

    return data.data;
  },
});

const {website, refetchWebsite} = useWebsite();

const {isSubscribedToKit} = useKit();

const store = useStore();
const onboardingStep = computed(() => store.getters['strategy/onboardingStep']);
const strategyId = computed(() => store.getters['strategy/strategyId']);
const showKeywordReviewModal = computed(() => store.getters['strategy/showKeywordReviewModal']);
const reviewAvailable = computed(() => store.getters['strategy/reviewAvailable']);
const reviewRequired = computed(() => store.getters['strategy/reviewRequired']);
const showKitModal = computed(() => store.getters['strategy/showKitModal']);

const isProcessingKeywordAutoAssign = computed(() => {
  if (keywordAutoAssignStatus.value?.autoAssignStatus === 'error' && keywordAutoAssignStatus.value?.acknowledgedCompleted) {
    return false;
  }

  if (['processing', 'pending', 'error'].includes(keywordAutoAssignStatus.value?.autoAssignStatus)) {
    return true;
  }

  return keywordAutoAssignStatus.value?.autoAssignStatus === 'completed' && !keywordAutoAssignStatus.value?.acknowledgedCompleted;
});

onMounted(() =>
  window.onbeforeunload = () => {
    if (this.reviewRequired) {
      return 'You have unsaved changes. Are you sure you want to leave?';
    }
  }
);

const reviewSitemap = () => {
  window.mixpanel('Sitemap: Review Sitemap');

  store.dispatch('strategy/openKeywordReviewModal');
}

const markUserAcknowledgedCompleted = () => {
  refetchWebsite();
  acknowledgedProgressCompleted();
}
</script>

<template>
  <div
    v-if="website"
    class="full_width sitemap_view default-container-width"
  >
    <SitemapProcessingKeywords
      v-if="isProcessingKeywordAutoAssign"
      @complete-auto-assign-process="markUserAcknowledgedCompleted"
    />
    <div v-else>
      <SitemapHeader />
      <SitemapTable />
      <div
        class="sitemap_options"
      >
        <DefaultButton
          v-if="!isSubscribedToKit"
          fixed-width
          size="md"
          class="mr-4"
          button-style="secondary"
          :disabled="!reviewAvailable"
          @click="reviewSitemap"
        >
          Review Sitemap
        </DefaultButton>
        <PrimaryButton
          v-if="onboardingStep === 'COMPLETED' && (!reviewRequired || isSubscribedToKit)"
          class="md"
          @click="store.dispatch('strategy/saveSitemap', {strategyId})"
        >
          Save Sitemap
        </PrimaryButton>
      </div>

      <ReviewSitemapKeywordsModal
        v-if="showKeywordReviewModal"
        @close="store.dispatch('strategy/closeKeywordReviewModal')"
      />
    </div>
  </div>
  <SitemapKitChangesModal
    v-if="showKitModal"
    @close="() => store.dispatch('strategy/dismissKitModal')"
  />
</template>

<style lang="scss" scoped>
.sitemap_view {
  display: flex;
  flex-direction: column;
}

.sitemap_options {
  display: flex;
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 1rem;
  background: white;
  justify-content: flex-end;
}
</style>
